<template>
  <div class="px-4 pt-6">
    <div class="flex items-center justify-between mb-4 flex-wrap gap-3">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Label print</h1>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Choose whether to load serials by <strong class="font-semibold text-gray-800 dark:text-gray-200">Purchase Order</strong> (current process)
          or <strong class="font-semibold text-gray-800 dark:text-gray-200">Purchase Receipt</strong> (older receipts).
        </p>
      </div>
      <div class="flex items-center space-x-3">
        <a href="/erp/serial_label_print/" class="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:ring-4 focus:ring-purple-300 text-base font-medium transition-colors duration-200 shadow-md hover:shadow-lg">
          🏷️ Print by Serial Numbers
        </a>
        <a href="/erp/packaging_print/" class="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-300 text-base font-medium transition-colors duration-200 shadow-md hover:shadow-lg">
          📦 Packaging Print
        </a>
        <a href="/erp/qlink-pallet-printer/" class="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-300 text-base font-medium transition-colors duration-200 shadow-md hover:shadow-lg">
          🟦 Pallet Printer
        </a>
      </div>
    </div>

    <div
      class="mb-6 rounded-xl border-2 border-blue-200 dark:border-blue-900/60 bg-blue-50/80 dark:bg-gray-800/80 p-4 sm:p-5 shadow-sm"
      role="region"
      aria-label="Document source for label printing"
    >
      <p class="text-sm font-semibold text-gray-900 dark:text-white mb-3">
        {{ pageTitle }}
      </p>
      <div class="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-900 p-1 shadow-inner">
        <button
          type="button"
          class="flex-1 sm:flex-none px-4 py-3 sm:py-2.5 text-sm font-medium rounded-md transition-colors text-center"
          :class="docType === 'Purchase Order'
            ? 'bg-blue-600 text-white shadow-sm'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'"
          :aria-pressed="docType === 'Purchase Order'"
          @click="setDocType('Purchase Order')"
        >
          Purchase Order
          <span class="block text-xs font-normal opacity-90 mt-0.5">New workflow · serials by PO</span>
        </button>
        <button
          type="button"
          class="flex-1 sm:flex-none px-4 py-3 sm:py-2.5 text-sm font-medium rounded-md transition-colors text-center border-t sm:border-t-0 sm:border-l border-gray-200 dark:border-gray-700 sm:ml-1"
          :class="docType === 'Purchase Receipt'
            ? 'bg-blue-600 text-white shadow-sm'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'"
          :aria-pressed="docType === 'Purchase Receipt'"
          @click="setDocType('Purchase Receipt')"
        >
          Purchase Receipt
          <span class="block text-xs font-normal opacity-90 mt-0.5">Legacy · goods receipt</span>
        </button>
      </div>
      <p class="mt-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        {{ modeHint }}
      </p>
    </div>

    <form class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-4" @submit.prevent="applyFilters">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-200">Name</label>
        <input v-model="filters.name" type="text" class="mt-1 block w-full pl-3 pr-10 py-2 border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" :placeholder="namePlaceholder" />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-200">Supplier</label>
        <input v-model="filters.supplier" type="text" class="mt-1 block w-full pl-3 pr-10 py-2 border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" placeholder="Supplier" />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-200">Created After</label>
        <input v-model="filters.date" type="date" class="mt-1 block w-full pl-3 pr-10 py-2 border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-200" for="label-print-page-size">Rows per page</label>
        <div class="mt-1 flex gap-2">
          <select
            id="label-print-page-size"
            v-model="pageSizeChoice"
            class="block w-full py-2 pl-3 pr-8 border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            @change="onPageSizeChoiceChange"
          >
            <option v-for="size in pageSizeOptions" :key="size" :value="String(size)">{{ size }}</option>
            <option value="custom">Custom</option>
          </select>
          <input
            v-if="pageSizeChoice === 'custom'"
            v-model="customPageSize"
            type="number"
            min="1"
            :max="maxPageSize"
            class="block w-24 py-2 pl-3 pr-2 border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            aria-label="Custom rows per page"
            @change="applyCustomPageSize"
          />
        </div>
      </div>
      <div class="flex items-end">
        <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300" :disabled="loading">Apply Filters</button>
      </div>
    </form>

    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 table-fixed dark:divide-gray-600">
        <thead class="bg-gray-100 dark:bg-gray-700">
          <tr>
            <th class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Name</th>
            <th class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Supplier</th>
            <th class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Creation</th>
            <th class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Open</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
          <tr v-for="pr in receipts" :key="pr.name" class="hover:bg-gray-100 dark:hover:bg-gray-700">
            <td class="p-4 text-sm text-gray-900 dark:text-white">{{ pr.name }}</td>
            <td class="p-4 text-sm text-gray-900 dark:text-white">{{ supplierDisplay(pr) }}</td>
            <td class="p-4 text-sm text-gray-900 dark:text-white">{{ pr.creation }}</td>
            <td class="p-4">
              <a
                class="text-blue-600 hover:underline dark:text-blue-400"
                :href="detailUrl(pr.name)"
                target="_blank"
                rel="noopener noreferrer"
              >Open</a>
            </td>
          </tr>
          <tr v-if="!loading && listError">
            <td colspan="4" class="p-4 text-sm text-red-600 dark:text-red-400">{{ listError }}</td>
          </tr>
          <tr v-else-if="!loading && !receipts.length">
            <td colspan="4" class="p-4 text-sm text-gray-500 dark:text-gray-400">{{ emptyMessage }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-3 flex items-center gap-2 flex-wrap">
      <button class="px-3 py-1.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50" :disabled="loading || page<=1" @click="changePage(page-1)">Prev</button>
      <span class="text-sm text-gray-600 dark:text-gray-300">Page {{ page }} / {{ totalPages }}<span v-if="total"> · {{ total }} {{ docNoun }}</span></span>
      <span v-if="loading" class="text-sm text-gray-500 dark:text-gray-400">Loading…</span>
      <button class="px-3 py-1.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50" :disabled="loading || page>=totalPages" @click="changePage(page+1)">Next</button>
    </div>
  </div>
</template>
<script>
export default {
  name: 'AppLabelPrintSelect',
  data() {
    return {
      docType: 'Purchase Order',
      filters: { name: '', supplier: '', date: '' },
      receipts: [],
      page: 1,
      pageSizeOptions: [10, 20, 50, 100, 250, 500],
      maxPageSize: 500,
      pageSizeChoice: '10',
      customPageSize: 10,
      pageSize: 10,
      totalPages: 1,
      total: 0,
      loading: false,
      listError: '',
      fetchSeq: 0,
    };
  },
  computed: {
    pageTitle() {
      return this.docType === 'Purchase Order'
        ? 'List purchase orders, then open one to print'
        : 'List purchase receipts, then open one to print';
    },
    modeHint() {
      if (this.docType === 'Purchase Order') {
        return 'Serial numbers are matched in the DMS by Purchase Order on each Serial No. Pick a PO below, then open it to print labels.';
      }
      return 'Use this for older flows where serials were tied to a Purchase Receipt. Pick a receipt below, then open it to print.';
    },
    namePlaceholder() {
      return this.docType === 'Purchase Order'
        ? 'Purchase Order name'
        : 'Purchase Receipt name';
    },
    emptyMessage() {
      return this.docType === 'Purchase Order'
        ? 'No purchase orders found.'
        : 'No purchase receipts found.';
    },
    docNoun() {
      return this.docType === 'Purchase Order' ? 'purchase orders' : 'purchase receipts';
    },
    listJsonPath() {
      return this.docType === 'Purchase Order'
        ? '/erp/purchase-orders.json'
        : '/erp/purchase-receipts.json';
    },
  },
  mounted() {
    this.fetchReceipts();
  },
  methods: {
    detailUrl(docName) {
      const base = `/erp/label_print/${encodeURIComponent(docName)}/`;
      const q = new URLSearchParams({ doc_type: this.docType });
      return `${base}?${q.toString()}`;
    },
    onDocTypeChange() {
      this.page = 1;
      this.fetchReceipts();
    },
    setDocType(next) {
      if (this.docType === next) return;
      this.docType = next;
      this.onDocTypeChange();
    },
    commitPageSize() {
      if (this.pageSizeChoice !== 'custom') {
        return parseInt(this.pageSizeChoice, 10);
      }
      const parsed = parseInt(this.customPageSize, 10);
      const size = Number.isFinite(parsed) && parsed >= 1
        ? Math.min(parsed, this.maxPageSize)
        : this.pageSize;
      this.customPageSize = size;
      if (this.pageSizeOptions.includes(size)) {
        this.pageSizeChoice = String(size);
      }
      return size;
    },
    onPageSizeChoiceChange() {
      if (this.pageSizeChoice === 'custom') {
        this.customPageSize = this.pageSize;
        return;
      }
      const size = this.commitPageSize();
      if (size === this.pageSize) return;
      this.pageSize = size;
      this.page = 1;
      this.fetchReceipts();
    },
    applyCustomPageSize() {
      const size = this.commitPageSize();
      if (size === this.pageSize) return;
      this.pageSize = size;
      this.page = 1;
      this.fetchReceipts();
    },
    async fetchReceipts() {
      const requestId = ++this.fetchSeq;
      this.loading = true;
      this.listError = '';
      const url = new URL(this.listJsonPath, window.location.origin);
      url.searchParams.set('name', this.filters.name || '');
      url.searchParams.set('supplier', this.filters.supplier || '');
      url.searchParams.set('date', this.filters.date || '');
      url.searchParams.set('page', String(this.page));
      url.searchParams.set('page_size', String(this.pageSize));
      try {
        const res = await fetch(url.toString(), { credentials: 'same-origin' });
        if (requestId !== this.fetchSeq) return;
        if (!res.ok) {
          let message = 'Could not load the list.';
          try {
            const body = await res.json();
            if (body && body.error) message = String(body.error);
          } catch (parseError) {
            /* keep the generic message */
          }
          throw new Error(message);
        }
        const data = await res.json();
        if (requestId !== this.fetchSeq) return;
        this.receipts = data.results || [];
        this.total = data.total || 0;
        this.totalPages = data.total_pages || 1;
        if (data.page_size && data.page_size !== this.pageSize) {
          this.pageSize = data.page_size;
          this.customPageSize = data.page_size;
          this.pageSizeChoice = this.pageSizeOptions.includes(data.page_size)
            ? String(data.page_size)
            : 'custom';
        }
      } catch (error) {
        if (requestId !== this.fetchSeq) return;
        this.receipts = [];
        this.total = 0;
        this.totalPages = 1;
        this.listError = error && error.message ? error.message : 'Could not load the list.';
      } finally {
        if (requestId === this.fetchSeq) this.loading = false;
      }
    },
    applyFilters() {
      const size = this.commitPageSize();
      this.pageSize = size;
      this.page = 1;
      this.fetchReceipts();
    },
    async changePage(p) {
      this.page = p;
      await this.fetchReceipts();
    },
    /** ERP: supplier = link / org key; supplier_name = human-readable label */
    supplierDisplay(row) {
      const id = (row.supplier || '').trim();
      const friendly = (row.supplier_name || '').trim();
      if (friendly && id && friendly !== id) {
        return `${friendly} (${id})`;
      }
      return friendly || id || '—';
    },
  }
}
</script>

